import React, { useEffect, useState } from "react";
import AdminSideMenu from '../../components/AdminSideMenu';
import api from "../../utils/axiosInstance";
import clsx from "clsx";

const FIELDS_TO_COMPARE_DRIVER = [
    'name', 'email', 'phone', 'job', 'cdl', 'city', 'state', 'address', 'zip', 'experience',
    'route', 'trailer', 'endorsements'
];
const FIELDS_TO_COMPARE_CARRIER = [
    'companyName', 'contactName', 'jobTitle', 'email', 'phone', 'address', 'city', 'state', 'zip', 'website', 'trailer', 'companyDesc'
];

const PendingApproval = () => {
    const [activeTab, setActiveTab] = useState("drivers");
    const [driverEdits, setDriverEdits] = useState([]);
    const [carrierEdits, setCarrierEdits] = useState([]);
    const [packageSubscribes, setPackageSubscribes] = useState([]);
    const [driversOrig, setDriversOrig] = useState({});
    const [carriersOrig, setCarriersOrig] = useState({});
    const [packagesOrig, setPackagesOrig] = useState({});
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ open: false, message: "" });

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get('/driverEdits'),
            api.get('/drivers'),
            api.get('/carrierEdits'),
            api.get('/carriers'),
            api.get('/packageSubscribed'), // fetch pending package requests
            api.get('/packages'),
        ])
            .then(([driverEditsRes, driversRes, carrierEditsRes, carriersRes, pkgSubRes, pkgsRes]) => {
                setDriverEdits(driverEditsRes.data || []);
                setCarrierEdits(carrierEditsRes.data || []);
                setPackageSubscribes(pkgSubRes.data || []);

                // Lookup maps
                const driversById = {};
                (driversRes.data || []).forEach(d => { driversById[d._id] = d });
                setDriversOrig(driversById);

                const carriersById = {};
                (carriersRes.data || []).forEach(c => { carriersById[c._id] = c });
                setCarriersOrig(carriersById);

                const pkgsById = {};
                (pkgsRes.data || []).forEach(pkg => { pkgsById[pkg._id] = pkg });
                setPackagesOrig(pkgsById);

                setLoading(false);
            })
            .catch(() => { setLoading(false); });
    }, []);

    function getChangedFields(edit, orig, fields) {
        // Only show the fields that changed
        const changed = [];
        (fields || Object.keys(edit)).forEach(field => {
            if (field === 'resume') return;
            let oldVal = orig?.[field];
            let newVal = edit?.[field];
            if (Array.isArray(oldVal) || Array.isArray(newVal)) {
                oldVal = (oldVal || []).join(', ');
                newVal = (newVal || []).join(', ');
            }
            if ((oldVal ?? '') !== (newVal ?? '')) {
                changed.push({ field, old: (oldVal ?? ''), new: (newVal ?? '') });
            }
        });
        return changed;
    }

    // --- Approve for driver/carrier ---
    const handleApprove = async (item, type) => {
        try {
            await api.post(`/${type}Edits/approve/${item._id}`
            );
            if (type === "driver") setDriverEdits(edits => edits.filter(e => e._id !== item._id));
            else setCarrierEdits(edits => edits.filter(e => e._id !== item._id));
        } catch (err) {
            alert('Failed to approve. Try again.');
        }
    };

    // --- Reject (remove only) for driver/carrier ---
    const handleReject = async (item, type) => {
        try {
            await api.delete(`/${type}Edits/${item._id}`);
            if (type === "driver") setDriverEdits(edits => edits.filter(e => e._id !== item._id));
            else setCarrierEdits(edits => edits.filter(e => e._id !== item._id));
        } catch (err) {
            alert('Failed to reject/delete. Try again.');
        }
    };

    // --- Package Approve ---
    const handlePackageApprove = async (item) => {
        try {
            await api.post(`/subscribed/approve/${item._id}`);
            setPackageSubscribes(list => list.filter(e => e._id !== item._id));
            setModal({ open: true, message: "Package Approved" });
        } catch (err) {
            let msg = 'Failed to approve package. Try again.';
            if (err.response?.data?.message) msg = err.response.data.message;
            alert(msg);
        }
    };

    // Reject: DELETE to /packageSubscribed/:id, then remove from UI state on success
    const handlePackageReject = async (item) => {
        try {
            await api.delete(`/subscribed/${item._id}`);
            setPackageSubscribes(list => list.filter(e => e._id !== item._id));
            setModal({ open: true, message: "Package Rejected" });
        } catch (err) {
            let msg = 'Failed to reject package request.';
            if (err.response?.data?.message) msg = err.response.data.message;
            alert(msg);
        }
    };

    const Modal = ({ open, message, onClose }) =>
        open ? (
            <div className="fixed top-0 left-0 w-full h-full z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white p-8 rounded shadow text-center min-w-[280px]">
                    <div className="mb-6 text-lg font-semibold">{message}</div>
                    <button
                        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
                        onClick={onClose}
                    >
                        OK
                    </button>
                </div>
            </div>
        ) : null;

    // --- Driver/Carrier Edit Card ---
    function renderEditCard(edit, orig, type) {
        const fields = type === "driver" ? FIELDS_TO_COMPARE_DRIVER : FIELDS_TO_COMPARE_CARRIER;
        const changed = getChangedFields(edit, orig, fields);

        // Show identificationNumber instead of email
        const identNumber = orig?.identificationNumber
            ? orig.identificationNumber
            : <span className="italic text-gray-400">No ID</span>;

        return (
            <div
                className={clsx(
                    "bg-white rounded-xl border border-blue-200 shadow-sm p-6 hover:bg-gray-50 mb-4"
                )}
                style={{ borderLeft: "4px solid red", minHeight: '80px' }}
                key={edit._id}
            >
                <div className="flex justify-between items-center">
                    <div>
                        <div className="font-bold text-lg text-gray-800 mb-2">
                            {type === "driver"
                                ? (orig?.name || "Driver") + " (" + identNumber + ")"
                                : (orig?.companyName || "Carrier") + " (" + identNumber + ")"}
                        </div>
                        <div className="text-base text-gray-700">
                            {changed.length === 0
                                ? <span className="italic text-gray-500">No data changed</span>
                                : (
                                    <ul className="list-disc pl-5">
                                        {changed.map(change =>
                                            <li key={change.field} className="mb-1">
                                                <b className="capitalize">{change.field}</b> changed:&nbsp;
                                                <span className="text-black">{change.old || <i>Empty</i>}</span>
                                                <span className="mx-2 text-blue-500 font-semibold">&#8594;</span>
                                                <span className="text-blue-500 font-semibold">{change.new || <i>Empty</i>}</span>
                                            </li>
                                        )}
                                    </ul>
                                )
                            }
                        </div>
                        {edit.resume && type === "driver" && (
                            <div className="text-xs mt-2">
                                <span className="font-semibold">Resume uploaded:</span>
                                {" "}
                                <a
                                    href={`/uploads/${edit.resume}`}
                                    className="text-blue-600 underline"
                                    target="_blank" rel="noopener noreferrer"
                                >
                                    View file
                                </a>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-2 min-w-[120px]">
                        <button
                            onClick={() => handleApprove(edit, type)}
                            className="bg-blue-600 text-white px-4 py-1 rounded mb-1 font-semibold hover:bg-blue-800"
                        >Approve</button>
                        <button
                            onClick={() => handleReject(edit, type)}
                            className="bg-black text-white px-4 py-1 rounded font-semibold hover:bg-gray-700"
                        >Reject</button>
                    </div>
                </div>
            </div>
        );
    }

    // --- Package Card ---
    function renderPackageCard(item) {
        const carrier = carriersOrig[item.carrierId] || {};
        const pkg = packagesOrig[item.packageId] || {};
        if (!carrier || !pkg) return null;

        // Show identificationNumber instead of email
        const identNumber = carrier?.identificationNumber || <span className="italic text-gray-400">No ID</span>;

        return (
            <div
                className="bg-white rounded-xl border border-blue-200 shadow-sm p-6 hover:bg-gray-50 mb-4"
                style={{ borderLeft: "4px solid #b80e0b", minHeight: '60px' }}
                key={item._id}
            >
                <div className="flex justify-between items-center">
                    <div>
                        <div className="font-bold text-lg text-gray-800 mb-2">
                            {pkg.name} <span className="text-gray-400">/</span> {carrier.companyName || "Carrier"}&nbsp;(
                            {identNumber}
                            )
                        </div>
                        <Modal
                            open={modal.open}
                            message={modal.message}
                            onClose={() => setModal({ open: false, message: "" })}
                        />
                    </div>
                    <div className="flex flex-col gap-2 min-w-[120px]">
                        <button
                            onClick={() => handlePackageApprove(item)}
                            className="bg-blue-600 text-white px-4 py-1 rounded mb-1 font-semibold hover:bg-blue-800"
                        >Approve</button>
                        <button
                            onClick={() => handlePackageReject(item)}
                            className="bg-black text-white px-4 py-1 rounded font-semibold hover:bg-gray-700"
                        >Reject</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col md:flex-row">
            <AdminSideMenu />
            <div className="md:ml-64 min-h-screen w-full bg-gray-50">
                <div className="p-4 md:p-8">
                    <header className="bg-truckers-gray z-10 pb-4 mt-4 md:mt-0">
                        <h1 className="text-3xl font-bold text-gray-900">Pending Approvals</h1>
                        <p className="text-gray-600">Review and approve/reject all profile edit requests</p>
                    </header>

                    <div className="flex mt-8 gap-3 border-b">
                        {/* <button
                            className={clsx("px-5 py-2 font-semibold border-b-4 mr-4",
                                activeTab === 'packages' ? "border-blue-700 text-blue-700" : "border-transparent text-gray-600")}
                            onClick={() => setActiveTab("packages")}
                        >
                            Packages ({packageSubscribes.length})
                        </button> */}
                        <button
                            className={clsx("px-5 py-2 font-semibold border-b-4 mr-4",
                                activeTab === 'drivers' ? "border-blue-700 text-blue-700" : "border-transparent text-gray-600")}
                            onClick={() => setActiveTab("drivers")}
                        >
                            Drivers ({driverEdits.length})
                        </button>
                        <button
                            className={clsx("px-5 py-2 font-semibold border-b-4",
                                activeTab === 'carriers' ? "border-blue-700 text-blue-700" : "border-transparent text-gray-600")}
                            onClick={() => setActiveTab("carriers")}
                        >
                            Carriers ({carrierEdits.length})
                        </button>
                    </div>

                    <section className="mt-6">
                        {loading && <div className="py-20 text-center text-gray-400 text-lg">Loading...</div>}
                        {/* {!loading && activeTab === 'packages' && (
                            <div>
                                {packageSubscribes.length === 0
                                    ? <div className="mt-10 text-gray-600">No pending package subscriptions.</div>
                                    : packageSubscribes.map(renderPackageCard)
                                }
                            </div>
                        )} */}

                        {!loading && activeTab === 'drivers' && (
                            <div>
                                {driverEdits.length === 0
                                    ? <div className="mt-10 text-gray-600">No pending driver approvals.</div>
                                    : driverEdits.map(edit =>
                                        renderEditCard(
                                            edit,
                                            driversOrig[edit.driverId],
                                            "driver"
                                        )
                                    )
                                }
                            </div>
                        )}
                        {!loading && activeTab === 'carriers' && (
                            <div>
                                {carrierEdits.length === 0
                                    ? <div className="mt-10 text-gray-600">No pending carrier approvals.</div>
                                    : carrierEdits.map(edit =>
                                        renderEditCard(
                                            edit,
                                            carriersOrig[edit.carrierId],
                                            "carrier"
                                        )
                                    )
                                }
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PendingApproval;
